import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import {
  zslExecutionFailedErrorMsgAtom,
  togglesAtom,
  sideBarSelectedAtom,
  isAiPageAtom,
  setMessagesAtom,
  isViewOnlyModeAtom,
} from "@zapier/canvas-state";
import type { UIMessage } from "@zapier/agent-system";

import { renderZslToCanvasCommand } from "../atoms/renderZslToCanvasCommand";
import { outlineAtom } from "../atoms/outlineAtom";
import { aiPromptAtom } from "../atoms/aiPromptAtom";
import { useZslPieceCompletion } from "./useZslPieceCompletion";
import {
  promptToZslErrorAtom,
  isPromptToZslCompleteAtom,
  isStreamingZslAtom,
} from "../atoms/streamingStatusAtoms";
import { tuningPromptsAtom } from "../atoms/tuningPromptsAtom";

export function usePromptToZslToCanvas() {
  const submitZslToCanvas = useSetAtom(renderZslToCanvasCommand);
  const setStringOutline = useSetAtom(outlineAtom);
  const [prompt, setPrompt] = useAtom(aiPromptAtom);
  const setTuningPrompts = useSetAtom(tuningPromptsAtom);
  const setError = useSetAtom(promptToZslErrorAtom);
  const setIsViewOnlyMode = useSetAtom(isViewOnlyModeAtom);
  const toggles = useAtomValue(togglesAtom);
  const setMessages = useSetAtom(setMessagesAtom);
  const setZslExecutionFailedErrorMsg = useSetAtom(
    zslExecutionFailedErrorMsgAtom,
  );
  const setIsStreamingZsl = useSetAtom(isStreamingZslAtom);
  const setSidebarSelected = useSetAtom(sideBarSelectedAtom);
  const setIsComplete = useSetAtom(isPromptToZslCompleteAtom);
  const isAiPage = useAtomValue(isAiPageAtom);

  const { handleSubmit } = useZslPieceCompletion({
    api: "/api/zsl/v2/combined_stream",
    async onMessage() {
      if (toggles.aiCanvasComposer && isAiPage) {
        setSidebarSelected("composer");

        // Set messages in the composer using the setMessagesAtom
        if (prompt) {
          const messages: UIMessage[] = [
            {
              role: "assistant",
              content: prompt,
              id: "user-prompt",
              createdAt: new Date(),
              parts: [
                { type: "text", text: prompt },
                {
                  type: "tool-invocation",
                  toolInvocation: {
                    state: "call",
                    toolName: "update_ui",
                    toolCallId: "update_ui",
                    args: "",
                  },
                },
              ],
            },
          ];
          setMessages(messages);
        }
      }
    },
    async onComplete(zsl) {
      setIsStreamingZsl(false);
      if (toggles.aiCanvasComposer && isAiPage) {
        setIsViewOnlyMode(false);
        // Add a tool invocation with state 'result' to the messages
        // Update the current messages using the setter
        setMessages((prevMessages) => {
          // Find the message with the 'update_ui' tool call
          return prevMessages.map((message) => {
            const hasUpdateUiCall = message.parts?.some(
              (part) =>
                part.type === "tool-invocation" &&
                part.toolInvocation?.toolName === "update_ui" &&
                part.toolInvocation?.state === "call",
            );

            if (hasUpdateUiCall) {
              // Create a new message with the updated parts
              return {
                ...message,
                parts: message.parts.map((part) => {
                  if (
                    part.type === "tool-invocation" &&
                    part.toolInvocation?.toolName === "update_ui" &&
                    part.toolInvocation?.state === "call"
                  ) {
                    // Return the updated tool invocation with state 'result'
                    return {
                      ...part,
                      toolInvocation: {
                        ...part.toolInvocation,
                        state: "result",
                        args: zsl,
                        result: {
                          content: [
                            {
                              type: "text",
                              text: "Canvas updated",
                            },
                          ],
                        },
                      },
                    };
                  }
                  return part;
                }),
              };
            }
            return message;
          });
        });
      }
      await submitZslToCanvas();
      setIsComplete(true);
      setTimeout(() => {}, 1000);
    },
    onError(error: string) {
      setError(error);
    },
  });

  const onPromptSubmit = useCallback(
    async (localPrompt: string) => {
      setIsComplete(false);
      setStringOutline(""); // When user modify the prompt
      setZslExecutionFailedErrorMsg(null); // Clear out old error alert

      setPrompt(localPrompt);
      setTuningPrompts([]); // Reset tuning prompts for each new AI suggestion
      await handleSubmit(localPrompt);
    },
    [
      handleSubmit,
      setZslExecutionFailedErrorMsg,
      setIsComplete,
      setPrompt,
      setStringOutline,
      setTuningPrompts,
    ],
  );

  return {
    onPromptSubmit,
  };
}
